// Brand Asset — ตารางแบรนด์ที่จับตาอยู่ พร้อมยอดผู้ติดตามรายช่องทาง
//
// หนึ่งแถว = หนึ่งแบรนด์ · หนึ่งคอลัมน์ = หนึ่งช่องทาง · ในช่องคือยอดผู้ติดตาม
// ที่กดไปเปิดช่องทางนั้นได้เลย — อ่านข้ามแถวก็เทียบแบรนด์กันได้ในคอลัมน์เดียวกัน
//
// แบรนด์ที่ปักหมุด = แบรนด์ที่เราดูแล อยู่บนสุดเสมอ และเป็นตัวตั้งให้ AI หาคู่แข่ง
// ทั้งหน้าเป็นฟอร์มเดียว ไม่มีปุ่มบันทึก — หยุดแก้แล้วส่งทั้งชุดไปทับของเดิมเอง
import { Fragment, useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Brand, BrandAssets, BrandCompetitor } from "@contracts/api";
import { apiErrorMessage } from "@shared/api/client";
import { IconPencil, IconRefresh, IconTrash } from "@shared/ui/icons";
import { cn } from "@shared/lib/utils";
import {
  assetKeys,
  type ChannelFollowers,
  fetchBrandAssets,
  fetchChannelFollowers,
  FOLLOWER_PLATFORMS,
  saveBrandAssets,
  type SuggestedCompetitor,
} from "../api/brand-assets-api";
import { CompetitorFinder } from "../components/competitor-finder";

/** คอลัมน์ของตาราง — ต้องตรงกับ ONLINE_PLATFORMS ฝั่ง server ไม่งั้นที่ AI เสนอมาจะตกหล่น */
const PLATFORMS = [
  "Facebook",
  "Instagram",
  "TikTok",
  "YouTube",
  "LINE OA",
  "X (Twitter)",
  "Website",
  "Shopee",
  "Lazada",
];

/** ยอดเก่าเกินนี้ = ตัวเลขจางลง เตือนว่าควรกดอัปเดตใหม่ */
const STALE_DAYS = 14;

const days = (iso?: string) =>
  iso ? Math.floor((Date.now() - Date.parse(iso)) / 86_400_000) : NaN;
const stale = (iso?: string) => days(iso) >= STALE_DAYS;

/** อายุข้อมูลแบบอ่านง่าย — ตัวเลขผู้ติดตามที่ไม่รู้ว่าดึงเมื่อไรก็ตีความไม่ได้ */
function since(iso: string): string {
  const d = days(iso);
  if (!isFinite(d)) return "";
  if (d <= 0) return "วันนี้";
  if (d === 1) return "เมื่อวาน";
  if (d < 30) return `${d} วันก่อน`;
  return `${Math.floor(d / 30)} เดือนก่อน`;
}

/** ย่อตัวเลขให้พอดีช่องตาราง — 26,678,404 อ่านยากในที่แคบ */
function compact(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n >= 100_000 ? 0 : 1)}K`;
  return String(n);
}

/** note มาจาก AI Suggest เท่านั้น — ไว้ให้คนตัดสินใจก่อนบันทึก ไม่ได้เก็บลงฐานข้อมูล */
type Row = BrandCompetitor & { note?: string };
type Form = { channels: BrandAssets["channels"]; competitors: Row[] };

const EMPTY_ROW: Row = { name: "", url: "", channels: [] };

export function BrandAssetPage({ brand }: { brand: Brand }) {
  const qc = useQueryClient();
  const { data, isPending, isError, error } = useQuery({
    queryKey: assetKeys.byBrand(brand.id),
    queryFn: () => fetchBrandAssets(brand.id),
    retry: false,
  });

  const [form, setForm] = useState<Form | null>(null);
  const [seeded, setSeeded] = useState<BrandAssets | null>(null);
  /**
   * ก้อนล่าสุดที่ "ส่งไปแล้ว" ในรูป JSON — ใช้ตัดสินว่ายังมีอะไรค้างต้องบันทึกไหม
   *
   * เทียบกับสิ่งที่ส่ง ไม่ใช่สิ่งที่ server ตอบกลับ เพราะ server ตัดช่องว่าง/แถวเปล่าทิ้ง
   * ก่อนเก็บ ถ้าเทียบกับคำตอบ ฟอร์มจะไม่มีวันเท่ากันและวนบันทึกไม่จบ
   */
  const [savedJson, setSavedJson] = useState("");
  /**
   * ก้อนที่บันทึกแล้วไม่ผ่าน — กันยิงซ้ำไม่จบ
   *
   * ถ้าไม่จำไว้ ฟอร์มจะยังต่างจากที่บันทึกได้อยู่ตลอด ตัวบันทึกอัตโนมัติจึงยิงใหม่
   * ทุก ๆ 900ms ไม่หยุด · แก้อะไรอีกทีค่อยลองใหม่ให้เอง
   */
  const [failedJson, setFailedJson] = useState("");
  const [msg, setMsg] = useState("");
  const [finderOpen, setFinderOpen] = useState(false);
  /** แถวที่กางแผงแก้ลิงก์อยู่ — ทีละแถวพอ ไม่งั้นตารางยาวเกิน */
  const [editing, setEditing] = useState<number | null>(null);
  const [loadingRow, setLoadingRow] = useState<number | null>(null);
  /** กำลังดึงยอดของทั้งตาราง — แยกจาก loadingRow ที่เป็นเลขแถวเดียว */
  const [bulk, setBulk] = useState(false);
  /** ผลที่เพิ่งดึง — ใช้แสดง error รายช่องทาง ส่วนตัวเลขเขียนลงฟอร์มเพื่อให้บันทึกได้ */
  const [live, setLive] = useState<Record<string, ChannelFollowers>>({});

  /**
   * ตั้งค่าตั้งต้นตอน render ไม่ใช่ใน effect — วิธีที่ React แนะนำสำหรับรีเซ็ต state
   * เมื่อข้อมูลเปลี่ยน (ทำใน effect จะ render สองรอบและเห็นฟอร์มเปล่าแวบหนึ่ง)
   */
  if (data && data !== seeded) {
    setSeeded(data);
    const next = { channels: data.channels, competitors: data.competitors };
    setForm(next);
    setSavedJson(JSON.stringify(next));
  }

  const save = useMutation({
    mutationFn: (f: Form) => saveBrandAssets(brand.id, f),
    onSuccess: (saved, sent) => {
      setMsg("");
      setSavedJson(JSON.stringify(sent));
      // ตั้ง seeded เป็นก้อนใหม่ด้วย ไม่งั้นบล็อกตั้งค่าตั้งต้นข้างบนจะยิงทับฟอร์ม
      // แล้วสิ่งที่พิมพ์ระหว่างรอ response จะหายไปกลางคัน
      setSeeded(saved);
      qc.setQueryData(assetKeys.byBrand(brand.id), saved);
    },
    onError: (e, sent) => {
      setFailedJson(JSON.stringify(sent));
      setMsg(apiErrorMessage(e));
    },
  });

  const update = useMutation({
    /** row = null คือดึงทั้งตาราง — เขียนผลกลับโดยเทียบจาก URL ไม่ใช่เลขแถว */
    mutationFn: (p: { row: number | null; channels: { platform: string; url: string }[] }) =>
      fetchChannelFollowers(brand.id, p.channels),
    onSuccess: (rows, vars) => {
      setLive((prev) => ({ ...prev, ...Object.fromEntries(rows.map((r) => [r.url, r])) }));
      const at = new Date().toISOString();
      const got = new Map(
        rows.filter((r) => r.followers !== null).map((r) => [r.url, r.followers]),
      );
      setForm((prev) =>
        !prev
          ? prev
          : {
              ...prev,
              competitors: prev.competitors.map((c, j) =>
                vars.row !== null && j !== vars.row
                  ? c
                  : {
                      ...c,
                      channels: c.channels.map((ch) =>
                        got.has(ch.url)
                          ? { ...ch, followers: got.get(ch.url) ?? null, followersAt: at }
                          : ch,
                      ),
                    },
              ),
            },
      );
      setMsg(got.size ? `อัปเดตได้ ${got.size} ช่องทาง` : "ดึงยอดผู้ติดตามไม่สำเร็จ");
    },
    onError: (e) => setMsg(apiErrorMessage(e)),
    onSettled: () => {
      setLoadingRow(null);
      setBulk(false);
    },
  });

  const dirty = !!form && !!savedJson && JSON.stringify(form) !== savedJson;

  /**
   * บันทึกเองเมื่อหยุดแก้ — หน้านี้ไม่มีปุ่มบันทึก
   *
   * หน่วงไว้ก่อนยิงเพราะการพิมพ์ชื่อแบรนด์หนึ่งชื่อคือการเปลี่ยน state หลายสิบครั้ง
   * ตัวจับเวลาถูกล้างทุกครั้งที่ฟอร์มขยับ คำขอจึงเกิดตอนหยุดพิมพ์แล้วเท่านั้น
   */
  useEffect(() => {
    if (!form || !dirty || save.isPending) return;
    if (JSON.stringify(form) === failedJson) return; // ก้อนเดิมที่เพิ่งพลาด อย่ายิงซ้ำ
    const t = setTimeout(() => save.mutate(form), 900);
    return () => clearTimeout(t);
  }, [form, dirty, failedJson, save]);

  const head = (
    <div className="content-head">
      <div>
        <div className="breadcrumb">{brand.name} / Brand &amp; Competitors</div>
        <h1>Brand &amp; Competitors</h1>
      </div>
      <div className="head-actions">
        {/* ไม่มีปุ่มบันทึก — บอกสถานะแทน ไม่งั้นคนไม่รู้ว่าที่แก้ไปติดแล้วหรือยัง */}
        <span className={cn("save-state", save.isError && "is-bad")}>
          {save.isPending
            ? "กำลังบันทึก…"
            : save.isError
              ? msg || "บันทึกไม่สำเร็จ"
              : dirty
                ? "กำลังจะบันทึก…"
                : msg || "บันทึกอัตโนมัติแล้ว"}
        </span>
      </div>
    </div>
  );

  if (isPending || isError || !form) {
    return (
      <>
        {head}
        <div className="panel">
          <p className="muted">
            {isError ? `โหลดข้อมูลไม่สำเร็จ: ${apiErrorMessage(error)}` : "กำลังโหลดข้อมูล…"}
          </p>
        </div>
      </>
    );
  }

  const setRows = (competitors: Row[]) => {
    setMsg("");
    setFailedJson("");
    // ปักหมุดขึ้นบนสุดเสมอ — เรียงตอนเก็บ ไม่ใช่ตอนแสดง ลำดับที่เห็นจึงตรงกับที่บันทึก
    const i = competitors.findIndex((c) => c.pinned);
    setForm({
      ...form,
      competitors:
        i > 0 ? [competitors[i], ...competitors.filter((_c, j) => j !== i)] : competitors,
    });
  };

  const patch = (i: number, next: Partial<Row>) =>
    setRows(form.competitors.map((c, j) => (j === i ? { ...c, ...next } : c)));

  const move = (i: number, by: number) => {
    const to = i + by;
    if (to < 0 || to >= form.competitors.length) return;
    const next = [...form.competitors];
    [next[i], next[to]] = [next[to], next[i]];
    setEditing(null);
    setRows(next);
  };

  /** ช่องทางของแถวนี้ที่ดึงยอดได้ — ต้องมีลิงก์และเป็นแพลตฟอร์มที่มี actor */
  const fetchable = (c: Row) =>
    c.channels.filter((ch) => ch.url.trim() && FOLLOWER_PLATFORMS.includes(ch.platform));

  /** ยืนยันก่อนเริ่ม เพราะการรัน Apify มีค่าใช้จ่ายต่อช่องทาง */
  const refresh = (row: number, c: Row) => {
    const list = fetchable(c);
    const ok = window.confirm(
      `ดึงยอดผู้ติดตามของ ${c.name || "แบรนด์นี้"} จำนวน ${list.length} ช่องทาง` +
        `\n(${list.map((x) => x.platform).join(", ")})` +
        `\n\nการดึงข้อมูลใช้ Apify ซึ่งมีค่าใช้จ่ายต่อช่องทาง — ยืนยันเพื่อเริ่ม`,
    );
    if (!ok) return;
    setMsg("");
    setLoadingRow(row);
    update.mutate({ row, channels: list });
  };

  /**
   * ดึงยอดผู้ติดตามของทุกแบรนด์ในตารางรวดเดียว
   *
   * ปุ่ม ⟳ รายแถวมีอยู่แล้ว แต่ตารางหนึ่งมีได้สิบแบรนด์ การไล่กดทีละแถวคือรอ Apify
   * จบทีละรอบสิบครั้ง · ส่งไปครั้งเดียว server จัดกลุ่มตามแพลตฟอร์มแล้วรันขนานให้
   *
   * ถามยืนยันเสมอ และบอกจำนวนช่องทางจริงที่จะดึง เพราะคิดเงินต่อช่องทาง
   */
  const refreshAll = () => {
    const list = form.competitors.flatMap((c) => fetchable(c));
    if (!list.length) {
      setMsg(
        "ยังไม่มีช่องทางที่ดึงยอดได้ — ต้องมีลิงก์ Facebook / Instagram / TikTok / YouTube ก่อน",
      );
      return;
    }
    const brands = form.competitors.filter((c) => fetchable(c).length).length;
    const byPlatform = new Map<string, number>();
    for (const ch of list) byPlatform.set(ch.platform, (byPlatform.get(ch.platform) ?? 0) + 1);
    const breakdown = [...byPlatform.entries()].map(([p, n]) => `${p} ${n}`).join(" · ");

    const ok = window.confirm(
      `ดึงยอดผู้ติดตามของ ${brands} แบรนด์ รวม ${list.length} ช่องทาง\n(${breakdown})` +
        `\n\nการดึงข้อมูลใช้ Apify ซึ่งมีค่าใช้จ่ายต่อช่องทาง — ยืนยันเพื่อเริ่ม`,
    );
    if (!ok) return;
    setMsg("");
    setBulk(true);
    update.mutate({ row: null, channels: list });
  };

  const addSuggested = (s: SuggestedCompetitor) => {
    const have = new Set(form.competitors.map((x) => x.name.trim().toLowerCase()));
    if (have.has(s.name.trim().toLowerCase())) return;
    setRows([
      ...form.competitors,
      { name: s.name, url: s.url, channels: s.channels, note: s.note },
    ]);
    setMsg("เพิ่มเข้ารายการแล้ว");
  };

  return (
    <>
      {head}

      <MonitorStatus rows={form.competitors} />

      <div className="panel asset-panel">
        <div className="asset-head">
          <h3>แบรนด์</h3>
          <div className="asset-head-btns">
            <button
              type="button"
              className="btn-outline inline-btn"
              onClick={() => setFinderOpen(true)}
            >
              ✨ AI Suggest
            </button>
            <button
              type="button"
              className="btn-outline inline-btn"
              disabled={bulk || loadingRow !== null}
              onClick={refreshAll}
            >
              <IconRefresh size={15} /> {bulk ? "กำลังดึงทั้งตาราง…" : "Refresh All Stat"}
            </button>
          </div>
        </div>
        <p className="muted small-note">
          ปักหมุด 📌 แบรนด์ที่เราดูแล ให้อยู่บนสุดและเป็นตัวตั้งให้ AI หาคู่แข่ง · ตัวเลขในตารางคือ
          ยอดผู้ติดตาม กดที่ตัวเลขเพื่อเปิดช่องทางนั้น · ทุกอย่างที่แก้บันทึกเองอัตโนมัติ
        </p>

        <div className="asset-table-wrap">
          <table className="asset-table">
            <thead>
              <tr>
                <th className="col-brand">แบรนด์</th>
                {PLATFORMS.map((p) => (
                  <th key={p}>{p}</th>
                ))}
                <th className="col-act" />
              </tr>
            </thead>
            <tbody>
              {form.competitors.map((c, i) => (
                <Fragment key={i}>
                  <tr className={cn(c.pinned && "is-pinned")}>
                    <td className="col-brand">
                      <div className="asset-brand-cell">
                        <button
                          type="button"
                          className={cn("pin-btn", c.pinned && "on")}
                          title={c.pinned ? "เลิกปักหมุด" : "ปักหมุดเป็นแบรนด์ที่เราดูแล"}
                          aria-pressed={!!c.pinned}
                          onClick={() =>
                            // ปักหมุดได้ทีละแบรนด์ — ปักอันใหม่ = ปลดอันเก่าอัตโนมัติ
                            setRows(
                              form.competitors.map((x, j) => ({
                                ...x,
                                pinned: j === i && !c.pinned,
                              })),
                            )
                          }
                        >
                          📌
                        </button>
                        <input
                          type="text"
                          placeholder="ชื่อแบรนด์"
                          value={c.name}
                          onChange={(e) => patch(i, { name: e.target.value })}
                        />
                        {/* ติ๊กจากตารางได้เลย ไม่ต้องเปิดสไลด์ทีละแถว — สีคือเข้ารายงาน เทาคือไม่ */}
                        <button
                          type="button"
                          className={cn("flag-btn", c.forAnalysis && "on")}
                          aria-pressed={!!c.forAnalysis}
                          title={
                            c.forAnalysis
                              ? "อยู่ในรายงาน Content Analysis — กดเพื่อเอาออก"
                              : "กดเพื่อใส่แบรนด์นี้ในรายงาน Content Analysis"
                          }
                          onClick={() => patch(i, { forAnalysis: !c.forAnalysis })}
                        >
                          📊
                        </button>
                      </div>
                    </td>

                    {PLATFORMS.map((p) => {
                      const ch = c.channels.find((x) => x.platform === p);
                      const url = ch?.url.trim() ?? "";
                      const l = url ? live[url] : undefined;
                      const n = l && l.followers !== null ? l.followers : ch?.followers;
                      const age = ch?.followersAt ? since(ch.followersAt) : "";
                      const tip =
                        l?.error ||
                        [n != null ? `${n.toLocaleString()} คน` : "", age && `ดึงเมื่อ ${age}`]
                          .filter(Boolean)
                          .join(" · ");

                      // ไม่มีช่องทางนี้ = ช่องว่างสีเทา ต่างจากมีช่องทางแต่ยังไม่รู้ยอด
                      if (!ch) return <td key={p} className="cell-none" />;
                      return (
                        <td key={p} className={cn(stale(ch.followersAt) && "is-stale")}>
                          {url ? (
                            <a
                              href={url}
                              target="_blank"
                              rel="noreferrer noopener"
                              title={tip || p}
                            >
                              {n == null ? "↗" : compact(n)}
                            </a>
                          ) : (
                            <span className="muted" title="ยังไม่มีลิงก์">
                              –
                            </span>
                          )}
                        </td>
                      );
                    })}

                    <td className="col-act">
                      <div className="asset-acts">
                        <button
                          type="button"
                          className="icon-btn"
                          title="เลื่อนขึ้น"
                          disabled={i === 0}
                          onClick={() => move(i, -1)}
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          className="icon-btn"
                          title="เลื่อนลง"
                          disabled={i === form.competitors.length - 1}
                          onClick={() => move(i, 1)}
                        >
                          ↓
                        </button>
                        <button
                          type="button"
                          className={cn("icon-btn", editing === i && "on")}
                          title="ตั้งค่าแบรนด์นี้"
                          onClick={() => setEditing(editing === i ? null : i)}
                        >
                          <IconPencil size={15} />
                        </button>
                        <button
                          type="button"
                          className="icon-btn"
                          title={
                            fetchable(c).length
                              ? `ดึงยอดผู้ติดตาม ${fetchable(c).length} ช่องทาง (มีค่าใช้จ่าย)`
                              : "ต้องมีลิงก์ของ Facebook / Instagram / TikTok / YouTube ก่อน"
                          }
                          disabled={bulk || loadingRow !== null || !fetchable(c).length}
                          onClick={() => refresh(i, c)}
                        >
                          {loadingRow === i ? "…" : <IconRefresh size={15} />}
                        </button>
                        <button
                          type="button"
                          className="icon-btn"
                          title="ลบแบรนด์นี้"
                          onClick={() => {
                            setEditing(null);
                            setRows(form.competitors.filter((_x, j) => j !== i));
                          }}
                        >
                          <IconTrash size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                </Fragment>
              ))}

              {!form.competitors.length && (
                <tr>
                  <td colSpan={PLATFORMS.length + 2} className="asset-empty">
                    <span className="muted">
                      ยังไม่มีแบรนด์ — กด AI Suggest เพื่อค้นหา หรือเพิ่มเองด้านล่าง
                    </span>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <button
          type="button"
          className="btn-outline inline-btn"
          onClick={() => setRows([...form.competitors, EMPTY_ROW])}
        >
          ＋&nbsp; เพิ่มแบรนด์
        </button>
      </div>

      <BrandDrawer
        row={editing === null ? null : (form.competitors[editing] ?? null)}
        onPatch={(next) => editing !== null && patch(editing, next)}
        onClose={() => setEditing(null)}
      />

      {finderOpen && (
        <CompetitorFinder
          brandId={brand.id}
          brandName={brand.name}
          added={new Set(form.competitors.map((c) => c.name.trim().toLowerCase()))}
          onAdd={addSuggested}
          onClose={() => setFinderOpen(false)}
        />
      )}
    </>
  );
}

/** ลิงก์ Facebook ของแถวนี้ — เงื่อนไขหนึ่งของการเข้ารายงาน */
const facebookUrl = (c: Row) =>
  (c.channels.find((x) => x.platform === "Facebook")?.url ?? "").trim();

/**
 * สรุปว่าตอนนี้เราตามแบรนด์ไหนอยู่แบบไหน และใครเข้ารายงานรายเดือนบ้าง
 *
 * ค่าพวกนี้อยู่ในสไลด์ตั้งค่าของแต่ละแถว ซึ่งเปิดได้ทีละแบรนด์ — ถ้าไม่มีที่สรุป
 * ก็ต้องเปิดทุกแถวไล่ดูถึงจะตอบได้ว่าเดือนนี้รายงานจะมีใครบ้าง
 *
 * เงื่อนไขเข้ารายงานเขียนไว้ตรง ๆ เพราะติ๊กอย่างเดียวไม่พอ ต้องมีลิงก์ Facebook ด้วย
 * (ดู server/services/report-feed.service.ts) — แถวที่ติ๊กแล้วแต่ยังไม่มีลิงก์คือ
 * กรณีที่คนกรอกคิดว่าเข้าแล้วแต่จริง ๆ ยังไม่เข้า จึงต้องเตือนแยกออกมา
 */
function MonitorStatus({ rows }: { rows: Row[] }) {
  const named = rows.filter((c) => c.name.trim());
  if (!named.length) return null;

  const monthly = named.filter((c) => c.monitor === "monthly");
  const campaign = named.filter((c) => c.monitor === "campaign");
  const inReport = named.filter((c) => c.forAnalysis && facebookUrl(c));
  const missingLink = named.filter((c) => c.forAnalysis && !facebookUrl(c));

  const chips = (list: Row[]) => (
    <div className="status-chips">
      {list.map((c) => (
        <span className="status-chip" key={c.name}>
          {c.pinned && "📌 "}
          {c.name}
        </span>
      ))}
    </div>
  );

  return (
    <div className="panel status-panel">
      <h3>สถานะการติดตาม</h3>

      <div className="status-row">
        <span className="status-label">ดูรายเดือน</span>
        {monthly.length ? (
          chips(monthly)
        ) : (
          <span className="muted small-note">ยังไม่มีแบรนด์ไหนตั้งไว้ว่าดูรายเดือน</span>
        )}
      </div>

      <div className="status-row">
        <span className="status-label">เฉพาะกิจกรรม</span>
        {campaign.length ? (
          chips(campaign)
        ) : (
          <span className="muted small-note">ยังไม่มีแบรนด์ไหนตั้งไว้ว่าตามเฉพาะกิจกรรม</span>
        )}
      </div>

      <div className="status-row">
        <span className="status-label">เข้ารายงาน 📊</span>
        {inReport.length ? (
          chips(inReport)
        ) : (
          <span className="muted small-note">ยังไม่มีแบรนด์ไหนเข้ารายงาน</span>
        )}
      </div>

      <p className="muted small-note">
        เงื่อนไขเข้ารายงาน: ติ๊ก “แบรนด์นี้สำหรับ Content ทำ Analysis” ในสไลด์ตั้งค่า
        <b> และ </b>
        มีลิงก์ Facebook · แบรนด์ที่เข้ารายงานคือชุดที่ Facebook Engagement Dashboard ใช้
      </p>

      {!!missingLink.length && (
        <p className="status-warn">
          ⚠️ ติ๊กไว้แต่ยังไม่มีลิงก์ Facebook จึงยังไม่เข้ารายงาน:{" "}
          <b>{missingLink.map((c) => c.name).join(", ")}</b>
        </p>
      )}
    </div>
  );
}

/** ตัวเลือกของ dropdown "ทีม Content ตามแบรนด์นี้แบบไหน" */
const MONITOR_MODES: { value: NonNullable<Row["monitor"]>; label: string }[] = [
  { value: "", label: "— ยังไม่ระบุ —" },
  { value: "campaign", label: "Content Team Monitor เฉพาะกิจกรรม" },
  { value: "monthly", label: "Content Team Monitor ดูรายเดือน" },
];

/**
 * แผงตั้งค่าของแบรนด์หนึ่งแถว — ลิงก์รายช่องทาง และธงว่าเอาเข้ารายงานไหม
 *
 * เป็นแผงสไลด์ ไม่ใช่แถวขยายในตาราง เพราะตารางกว้างเกินจอตั้งแต่ 9 คอลัมน์แล้ว
 * แถวขยายจึงถูกคอลัมน์ที่ตรึงไว้ทับ และช่องกรอกก็แคบจนอ่านลิงก์ไม่จบ
 *
 * แก้แล้วมีผลกับฟอร์มทันที และหน้าบันทึกให้เองเมื่อหยุดแก้ — ปิดแผงคือปิดเฉย ๆ
 * ไม่ต้องยืนยันอะไร
 */
function BrandDrawer({
  row,
  onPatch,
  onClose,
}: {
  row: Row | null;
  onPatch: (next: Partial<Row>) => void;
  onClose: () => void;
}) {
  useEffect(() => {
    if (!row) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [row, onClose]);

  return (
    <>
      <div className={cn("drawer-overlay", row && "open")} onClick={onClose} />
      <aside className={cn("drawer", row && "open")}>
        <div className="drawer-head">
          <h3>{row?.name?.trim() || "ตั้งค่าแบรนด์"}</h3>
          <button type="button" className="icon-btn" title="ปิด" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="drawer-body">
          {row && (
            <>
              <div className="drawer-section">
                <h4>ชื่อแบรนด์</h4>
                <input
                  type="text"
                  className="drawer-input"
                  placeholder="ชื่อแบรนด์"
                  value={row.name}
                  onChange={(e) => onPatch({ name: e.target.value })}
                />

                <h4>ทีม Content ตามแบรนด์นี้แบบไหน</h4>
                <select
                  className="drawer-input"
                  value={row.monitor ?? ""}
                  onChange={(e) => onPatch({ monitor: e.target.value as Row["monitor"] })}
                >
                  {MONITOR_MODES.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>

                <label className="asset-check">
                  <input
                    type="checkbox"
                    checked={!!row.forAnalysis}
                    onChange={(e) => onPatch({ forAnalysis: e.target.checked })}
                  />
                  <span>
                    <b>แบรนด์นี้สำหรับ Content ทำ Analysis</b>
                    <span className="muted small-note">
                      ติ๊กไว้เพื่อบอกว่าแบรนด์นี้ต้องเอาเข้ารายงานวิเคราะห์คอนเทนต์
                    </span>
                  </span>
                </label>
              </div>

              <div className="drawer-section">
                <h4>ลิงก์ของแต่ละช่องทาง</h4>
                <p className="muted small-note">ล้างลิงก์ให้ว่าง = เอาช่องทางนั้นออกจากแบรนด์นี้</p>
                <div className="asset-links">
                  {PLATFORMS.map((p) => {
                    const ch = row.channels.find((x) => x.platform === p);
                    return (
                      <label className="asset-link-line" key={p}>
                        <span className="asset-link-label">{p}</span>
                        <input
                          type="url"
                          placeholder={`ลิงก์ ${p}`}
                          value={ch?.url ?? ""}
                          onChange={(e) => {
                            const v = e.target.value;
                            const rest = row.channels.filter((x) => x.platform !== p);
                            onPatch({
                              channels: v.trim()
                                ? [...rest, { ...(ch ?? { platform: p }), platform: p, url: v }]
                                : rest,
                            });
                          }}
                        />
                      </label>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
      </aside>
    </>
  );
}
